import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaUpload,
  FaImage,
  FaRupeeSign,
  FaPercentage,
  FaBox,
  FaClipboardList,
  FaIndustry,
  FaCheckCircle,
  FaTimes,
  FaArrowLeft,
  FaSave,
  FaRedo,
  FaTimesCircle,
  FaInfoCircle,
  FaShieldAlt
} from 'react-icons/fa';
import { GiMedicinePills } from 'react-icons/gi';

// Firebase imports
import { database, storage } from '../firebase'; // Adjust path if needed
import { ref as dbRef, push, get, set, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const AdminProductAdd = () => {
  const navigate = useNavigate();
  const { productId } = useParams(); // Get product ID from URL for edit mode
  const isEditMode = Boolean(productId);

  const [formData, setFormData] = useState({
    productName: '',
    price: '',
    discountType: 'percentage',
    discountValue: '',
    description: '',
    stockQuantity: '',
    status: 'In Stock',
    manufacturer: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const statusOptions = ['In Stock', 'Out of Stock', 'Low Stock', 'Coming Soon', 'Discontinued'];
  const discountTypes = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'amount', label: 'Fixed Amount' },
    { value: 'none', label: 'No Discount' }
  ];

  // Fetch product data if in edit mode
  useEffect(() => {
    if (isEditMode && productId) {
      const fetchProduct = async () => {
        setIsLoading(true);
        try {
          const productRef = dbRef(database, `Dr/products/${productId}`);
          const snapshot = await get(productRef);
          
          if (snapshot.exists()) {
            const productData = snapshot.val();
            setFormData({
              productName: productData.productName || '',
              price: productData.price || '',
              discountType: productData.discountType || 'percentage',
              discountValue: productData.discountValue || '',
              description: productData.description || '',
              stockQuantity: productData.stockQuantity || '',
              status: productData.status || 'In Stock',
              manufacturer: productData.manufacturer || ''
            });
            
            if (productData.imageUrl) {
              setExistingImageUrl(productData.imageUrl);
              setImagePreview(productData.imageUrl);
            }
          } else {
            setErrors({ submit: 'Product not found' });
            setTimeout(() => navigate('/admin/products'), 2000);
          }
        } catch (error) {
          console.error('Error fetching product:', error);
          setErrors({ submit: 'Failed to load product data' });
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchProduct();
    }
  }, [isEditMode, productId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-calculate discounted price
    const updatedFormData = { ...formData, [name]: value };
    if ((name === 'price' || name === 'discountValue' || name === 'discountType') && updatedFormData.price && (updatedFormData.discountValue || updatedFormData.discountType === 'none')) {
      let finalPrice = parseFloat(updatedFormData.price) || 0;
      
      if (updatedFormData.discountType === 'percentage' && updatedFormData.discountValue) {
        const discount = parseFloat(updatedFormData.discountValue) || 0;
        finalPrice = finalPrice - (finalPrice * discount / 100);
      } else if (updatedFormData.discountType === 'amount' && updatedFormData.discountValue) {
        finalPrice = finalPrice - (parseFloat(updatedFormData.discountValue) || 0);
      }
      
      setFormData(prev => ({ ...prev, finalPrice: finalPrice.toFixed(2) }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, productImage: 'File size must be less than 5MB' }));
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, productImage: 'Only JPG, PNG, WebP allowed' }));
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    if (errors.productImage) {
      setErrors(prev => ({ ...prev, productImage: '' }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageFile(null);
  };

  const validateForm = () => {
    const newErrors = {};

    // In edit mode, image is optional if there's an existing image
    if (!imageFile && !existingImageUrl) newErrors.productImage = 'Product image is required';
    if (!formData.productName.trim()) newErrors.productName = 'Product name is required';
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = 'Valid price required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.stockQuantity || parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = 'Valid stock quantity required';
    }

    if (formData.discountType !== 'none' && formData.discountValue) {
      const discount = parseFloat(formData.discountValue);
      if (formData.discountType === 'percentage' && (discount < 0 || discount > 100)) {
        newErrors.discountValue = 'Percentage must be 0-100';
      }
      if (formData.discountType === 'amount' && discount >= parseFloat(formData.price || 0)) {
        newErrors.discountValue = 'Discount cannot be ≥ price';
      }
    }

    return newErrors;
  };
  
const handleSubmit = async (e) => {
  e.preventDefault();

  const validationErrors = validateForm();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setIsSubmitting(true);

  try {
    let imageUrl = existingImageUrl; // Use existing image URL by default
    let currentProductId = productId; // Use existing product ID in edit mode

    // If in add mode, generate new product ID
    if (!isEditMode) {
      const productsRef = dbRef(database, "Dr/products");
      const snapshot = await get(productsRef);

      let nextId = 1;
      if (snapshot.exists()) {
        const ids = Object.keys(snapshot.val()).map(Number);
        nextId = Math.max(...ids) + 1;
      }
      currentProductId = nextId.toString();
    }

    // Upload new image if one was selected
    if (imageFile) {
      const imageRef = storageRef(
        storage,
        `Dr/products/${currentProductId}/image.jpg`
      );
      await uploadBytes(imageRef, imageFile);
      imageUrl = await getDownloadURL(imageRef);
    }

    // Calculate final price
    let finalPrice = parseFloat(formData.price);

    if (formData.discountType === "percentage" && formData.discountValue) {
      finalPrice -= (finalPrice * parseFloat(formData.discountValue)) / 100;
    } else if (formData.discountType === "amount" && formData.discountValue) {
      finalPrice -= parseFloat(formData.discountValue);
    }

    // Prepare product object
    const productData = {
      productId: currentProductId,
      productName: formData.productName.trim(),
      price: parseFloat(formData.price),
      discountType: formData.discountType,
      discountValue: formData.discountValue
        ? parseFloat(formData.discountValue)
        : 0,
      finalPrice: parseFloat(finalPrice.toFixed(2)),
      description: formData.description.trim(),
      stockQuantity: parseInt(formData.stockQuantity),
      status: formData.status,
      manufacturer: formData.manufacturer.trim() || "Not specified",
      imageUrl,
      addedAt: isEditMode ? undefined : new Date().toISOString(), // Don't update addedAt in edit mode
      updatedAt: isEditMode ? new Date().toISOString() : undefined // Add updatedAt in edit mode
    };

    // Remove undefined fields
    Object.keys(productData).forEach(key => 
      productData[key] === undefined && delete productData[key]
    );

    // Save or update product
    const productRef = dbRef(database, `Dr/products/${currentProductId}`);
    await set(productRef, productData);

    setSuccessMessage(isEditMode ? "Product updated successfully!" : "Product added successfully!");

    setTimeout(() => {
      navigate("/admin/products");
    }, 2000);

  } catch (error) {
    console.error(isEditMode ? "Error updating product:" : "Error adding product:", error);
    setErrors({ submit: isEditMode ? "Failed to update product. Please try again." : "Failed to add product. Please try again." });
  } finally {
    setIsSubmitting(false);
  }
};


  const handleReset = () => {
    setFormData({
      productName: '',
      price: '',
      discountType: 'percentage',
      discountValue: '',
      description: '',
      stockQuantity: '',
      status: 'In Stock',
      manufacturer: ''
    });
    setImagePreview(null);
    setImageFile(null);
    setErrors({});
    setSuccessMessage('');
  };

  const handleCancel = () => navigate('/admin/products');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading product data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleCancel} className="flex items-center text-teal-600 hover:text-teal-700 font-medium mb-4">
            <FaArrowLeft className="mr-2" /> Back to Products
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
          <p className="text-gray-600 mt-2">{isEditMode ? 'Update product details below.' : 'Fill in the details. Fields with * are required.'}</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
            <FaCheckCircle className="text-green-600 text-xl mr-3" />
            <div>
              <p className="font-bold text-green-800">{successMessage}</p>
              <p className="text-sm text-green-600">Redirecting...</p>
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
            <FaTimesCircle className="text-red-600 text-xl mr-3" />
            <p className="font-bold text-red-800">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Upload */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaImage className="mr-3 text-teal-600" /> Product Image *
                </h2>

                {imagePreview ? (
                  <div className="relative mb-6">
                    <img src={imagePreview} alt="Preview" className="w-full h-64 object-cover rounded-2xl" />
                    <button type="button" onClick={removeImage}
                      className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 shadow-lg"
                      title="Remove image">
                      <FaTimes />
                    </button>
                    {/* Change Image Button - appears on hover */}
                    <label className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-2xl flex items-center justify-center">
                      <div className="bg-white px-6 py-3 rounded-xl font-semibold text-gray-800 flex items-center shadow-lg">
                        <FaUpload className="mr-2" />
                        {isEditMode ? 'Change Image' : 'Replace Image'}
                      </div>
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                ) : (
                  <label className="block cursor-pointer mb-6">
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:border-teal-500 hover:bg-teal-50">
                      <FaUpload className="mx-auto text-5xl text-teal-600 mb-4" />
                      <p className="font-semibold text-gray-700">Click to upload image</p>
                      <p className="text-sm text-gray-500 mt-2">JPG, PNG, WebP • Max 5MB</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                )}

                {errors.productImage && <p className="text-red-600 text-sm">{errors.productImage}</p>}
                
                {/* Helpful hint for edit mode */}
                {isEditMode && imagePreview && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <FaInfoCircle className="mr-1" />
                    Hover over the image to change it
                  </p>
                )}

                {/* Price Summary */}
                <div className="mt-6 p-5 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl">
                  <h4 className="font-bold mb-3">Price Preview</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between"><span>Original:</span> <strong>₹{formData.price || '0'}</strong></div>
                    {formData.discountValue && formData.discountType !== 'none' && (
                      <div className="flex justify-between text-red-600">
                        <span>Discount:</span>
                        <strong>-{formData.discountType === 'percentage' ? `${formData.discountValue}%` : `₹${formData.discountValue}`}</strong>
                      </div>
                    )}
                    <div className="pt-2 border-t border-teal-200 flex justify-between text-lg font-bold">
                      <span>Final Price:</span>
                      <span className="text-emerald-700">₹{formData.finalPrice || formData.price || '0'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Product Name */}
                  <div className="md:col-span-2">
                    <label className="block font-medium text-gray-700 mb-2">Product Name *</label>
                    <div className="relative">
                      <GiMedicinePills className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="productName" value={formData.productName} onChange={handleChange}
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.productName ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="e.g., Vitamin D3 Capsules"
                      />
                    </div>
                    {errors.productName && <p className="text-red-600 text-sm mt-1">{errors.productName}</p>}
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Price (₹) *</label>
                    <div className="relative">
                      <FaRupeeSign className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="number" name="price" value={formData.price} onChange={handleChange}
                        min="0" step="0.01"
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.price && <p className="text-red-600 text-sm mt-1">{errors.price}</p>}
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Discount Type</label>
                    <select name="discountType" value={formData.discountType} onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500">
                      {discountTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  {/* Discount Value */}
                  {formData.discountType !== 'none' && (
                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        {formData.discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount (₹)'}
                      </label>
                      <div className="relative">
                        {formData.discountType === 'percentage' ? <FaPercentage className="absolute left-3 top-3.5 text-gray-400" /> : <FaRupeeSign className="absolute left-3 top-3.5 text-gray-400" />}
                        <input
                          type="number" name="discountValue" value={formData.discountValue} onChange={handleChange}
                          min="0"
                          className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.discountValue ? 'border-red-300' : 'border-gray-300'}`}
                        />
                      </div>
                      {errors.discountValue && <p className="text-red-600 text-sm mt-1">{errors.discountValue}</p>}
                    </div>
                  )}

                  {/* Stock Quantity */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Stock Quantity *</label>
                    <div className="relative">
                      <FaBox className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="number" name="stockQuantity" value={formData.stockQuantity} onChange={handleChange}
                        min="0"
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.stockQuantity ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.stockQuantity && <p className="text-red-600 text-sm mt-1">{errors.stockQuantity}</p>}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Status</label>
                    <select name="status" value={formData.status} onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500">
                      {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Manufacturer */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Manufacturer</label>
                    <div className="relative">
                      <FaIndustry className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="manufacturer" value={formData.manufacturer} onChange={handleChange}
                        className="pl-10 w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g., Sun Pharma"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block font-medium text-gray-700 mb-2">Description *</label>
                    <div className="relative">
                      <FaClipboardList className="absolute left-3 top-4 text-gray-400" />
                      <textarea
                        name="description" value={formData.description} onChange={handleChange}
                        rows="5"
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Key features, benefits, usage instructions..."
                      />
                    </div>
                    {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                  </div>
                </div>

                {/* Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex gap-3">
                    <button type="button" onClick={handleCancel}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                    <button type="button" onClick={handleReset}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaRedo className="mr-2" /> Reset
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-8 py-3 rounded-xl font-bold text-white flex items-center justify-center ${
                      isSubmitting
                        ? 'bg-teal-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:shadow-lg'
                    } transition-all`}
                  >
                    {isSubmitting ? (
                      <>{isEditMode ? 'Updating...' : 'Uploading & Saving...'}</>
                    ) : (
                      <><FaSave className="mr-2" /> {isEditMode ? 'Update Product' : 'Save Product'}</>
                    )}
                  </button>
                </div>

                {/* Tips */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <h4 className="font-medium flex items-center mb-2">
                    <FaInfoCircle className="mr-2 text-blue-600" /> Tips
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Use high-quality product images</li>
                    <li>• Accurate stock prevents overselling</li>
                    <li>• Clear description improves sales</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductAdd;